import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2, Loader2, Users, TrendingDown, Ticket, Save, ListChecks, Clock, Layers, Share2,
  CheckCircle2, LayoutTemplate, Lock, Send, Trash2, Plus, CreditCard, Copy, Store, Store as StoreIco,
  Target, Tag, Palette, Crosshair, ChevronDown, ChevronUp, MapPin,
} from "lucide-react";
import { sr } from "../../lib/sr";
import AdvancedTargeting from "../../components/smartreach/AdvancedTargeting";

const money = (n) => "$" + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const OWNER_TYPES = [
  { key: "single_unit", label: "Single-unit owner", icon: Store, desc: "One location. AI runs your single storefront's growth." },
  { key: "multi_unit", label: "Multi-unit owner", icon: Building2, desc: "Several stores. Set targeting, offers & creative per store." },
  { key: "franchisor", label: "Franchisor", icon: Layers, desc: "Corporate brand across franchisees — templates & co-op sign-off." },
];

export default function Franchise() {
  const [settings, setSettings] = useState(null);
  const [data, setData] = useState(null);
  const [coop, setCoop] = useState(null);
  const [savingType, setSavingType] = useState(false);

  const loadSettings = () => sr.franchiseSettings().then(setSettings).catch(() => setSettings({ owner_type: "single_unit", brand_kit: {} }));
  const load = () => sr.franchiseOverview().then(setData).catch(() => setData({ error: true }));
  const loadCoop = () => sr.coopList().then(setCoop).catch(() => {});
  useEffect(() => { loadSettings(); load(); loadCoop(); }, []);

  const setType = async (owner_type) => {
    if (owner_type === settings?.owner_type) return;
    setSavingType(true);
    try {
      await sr.setOwnerType(owner_type);
      setSettings((s) => ({ ...s, owner_type }));
      toast.success(`Switched to ${OWNER_TYPES.find((o) => o.key === owner_type)?.label}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    finally { setSavingType(false); }
  };

  if (!settings || !data) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  if (data.error) return <div className="sr-card"><p className="sr-muted">Could not load the command center.</p></div>;

  const ownerType = settings.owner_type || "single_unit";
  const isMulti = ownerType === "multi_unit" || ownerType === "franchisor";
  const t = data.totals || {};

  return (
    <div data-testid="franchise-page">
      <div style={{ marginBottom: 16 }}>
        <span className="sr-badge" style={{ marginBottom: 8 }}><Store size={12} /> Stores &amp; Franchise</span>
        <h1 style={{ fontSize: 30 }}>{settings.business || "Your business"}</h1>
        <p className="sr-muted" style={{ marginTop: 6 }}>Tell us how your business is structured — we tailor targeting, offers, creative and the whole store rollup to match.</p>
      </div>

      {/* Owner type selector */}
      <div className="sr-card" style={{ marginBottom: 20 }} data-testid="owner-type-card">
        <div className="sr-eyebrow" style={{ marginBottom: 10 }}>How is your business set up?</div>
        <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))", gap: 12 }}>
          {OWNER_TYPES.map((o) => {
            const active = ownerType === o.key;
            const Ico = o.icon;
            return (
              <button key={o.key} className="sr-card sr-card-hover" onClick={() => setType(o.key)} disabled={savingType}
                data-testid={`owner-type-${o.key}`} style={{ cursor: "pointer", textAlign: "left", background: active ? "var(--blue-light)" : "#fff", borderColor: active ? "var(--blue)" : "var(--border)", borderWidth: active ? 2 : 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "var(--navy)", fontWeight: 800 }}><Ico size={17} style={{ color: "var(--blue)" }} /> {o.label}</span>
                  {active && <CheckCircle2 size={18} color="var(--blue)" />}
                </div>
                <p className="sr-muted" style={{ fontSize: 12.5, marginTop: 6 }}>{o.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {!isMulti ? (
        <div className="sr-card" data-testid="single-unit-note" style={{ textAlign: "center", padding: 40, maxWidth: 620, margin: "0 auto" }}>
          <Store size={30} style={{ color: "var(--blue)" }} />
          <h3 style={{ margin: "12px 0 4px", fontSize: 20 }}>You're set up as a single storefront</h3>
          <p className="sr-muted" style={{ fontSize: 14 }}>Run your growth from the Growth Plan and Campaigns. When you open more locations, switch to <b>Multi-unit owner</b> above to add stores, target and price each one, and roll everything up here.</p>
          <button className="sr-btn sr-btn-primary" style={{ marginTop: 14 }} onClick={() => setType("multi_unit")} data-testid="become-multi-btn">
            <Plus size={15} /> I have multiple locations
          </button>
        </div>
      ) : (
        <>
          {/* Totals */}
          <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, marginBottom: 20 }} data-testid="franchise-totals">
            <Stat label="Stores" value={t.stores} icon={StoreIco} />
            <Stat label="Media budget" value={money(t.media_budget)} />
            {ownerType === "franchisor" && <Stat label="Co-op pool" value={money(t.co_op_pool)} accent />}
            <Stat label="Total visits" value={Number(t.visits || 0).toLocaleString()} icon={Users} />
            <Stat label="Cost / visit" value={t.cost_per_visit != null ? "$" + t.cost_per_visit : "—"} icon={TrendingDown} />
            <Stat label="Redemption rate" value={(t.redemption_rate ?? 0) + "%"} icon={Ticket} />
            <Stat label="Campaigns" value={Number(t.campaigns || 0).toLocaleString()} icon={ListChecks} />
            <Stat label="Awaiting sign-off" value={t.pending_approvals ?? 0} icon={Clock} accent={!!t.pending_approvals} />
          </div>

          {/* Multi-store look & feel (brand kit) */}
          <BrandKitCard initial={settings.brand_kit || {}} onSaved={(bk) => setSettings((s) => ({ ...s, brand_kit: bk }))} />

          {/* Store manager: add stores, per-store targeting + offer */}
          <StoresManager ownerType={ownerType} onChanged={load} />

          {/* Approvals rollup */}
          <div className="sr-card" style={{ marginBottom: 20 }} data-testid="franchise-approvals">
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <Clock size={16} style={{ color: "var(--blue)" }} />
              <strong style={{ color: "var(--navy)" }}>Awaiting sign-off across all stores</strong>
              <span className="sr-badge sr-badge-amber">{(data.approvals || []).length}</span>
            </div>
            {(data.approvals || []).length === 0 ? (
              <p className="sr-muted" style={{ fontSize: 13 }} data-testid="franchise-approvals-empty">Nothing waiting on a client decision. 🎉</p>
            ) : (
              <div style={{ display: "grid", gap: 8 }}>
                {data.approvals.map((a) => (
                  <div key={a.id} className="sr-card" data-testid={`franchise-approval-${a.id}`} style={{ background: "#fff", display: "flex", alignItems: "center", gap: 12, padding: "10px 14px" }}>
                    <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                      <div style={{ fontSize: 14, color: "var(--navy)", fontWeight: 600, textTransform: "capitalize" }}>{a.campaign_name}</div>
                      <div className="sr-muted" style={{ fontSize: 12 }}>{a.location_name} · waiting {Math.round(a.waiting_hours)}h</div>
                    </div>
                    <Link to={`/app/campaigns/${a.campaign_id}/project`} className="sr-btn sr-btn-ghost sr-btn-sm" data-testid={`franchise-approval-open-${a.id}`}>Open</Link>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Regional rollup */}
          {(data.regions || []).length > 0 && (
            <div className="sr-card" style={{ marginBottom: 20 }} data-testid="franchise-regions">
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <Layers size={16} style={{ color: "var(--blue)" }} />
                <strong style={{ color: "var(--navy)" }}>By region</strong>
                <span className="sr-badge sr-badge-gray">{data.regions.length}</span>
              </div>
              <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12 }}>
                {data.regions.map((r) => (
                  <div key={r.region} className="sr-card" data-testid={`region-${r.region}`} style={{ background: "#fff" }}>
                    <div style={{ color: "var(--navy)", fontWeight: 800, fontSize: 15 }}>{r.region}</div>
                    <div className="sr-muted" style={{ fontSize: 12, marginTop: 4 }}>{r.stores} store{r.stores === 1 ? "" : "s"} · {r.campaigns} campaigns</div>
                    <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                      <div><div className="sr-muted" style={{ fontSize: 11 }}>Spend</div><div style={{ fontWeight: 700, color: "var(--navy)" }}>{money(r.spend)}</div></div>
                      <div><div className="sr-muted" style={{ fontSize: 11 }}>Visits</div><div style={{ fontWeight: 700, color: "var(--navy)" }}>{Number(r.visits).toLocaleString()}</div></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Franchisor-only: co-op + corporate templates */}
          {ownerType === "franchisor" && (
            <>
              <CoopCampaigns coop={coop} reload={() => { loadCoop(); load(); }} />
              <CorporateTemplates stores={(data.stores || []).map((s) => ({ location_id: s.location_id, name: s.name, media_budget: s.media_budget }))} />
            </>
          )}
        </>
      )}
    </div>
  );
}

/* ---------------- Brand kit (multi-store look & feel) ---------------- */
const FONTS = ["Inter", "Poppins", "Montserrat", "Playfair Display", "Space Grotesk", "Roboto Slab", "Oswald", "Lato"];

function BrandKitCard({ initial, onSaved }) {
  const [bk, setBk] = useState({ logo_url: "", primary_color: "#0B2447", accent_color: "#E0A94A", font: "Inter", tagline: "", ...initial });
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setBk((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { const r = await sr.saveBrandKit(bk); onSaved?.(r.brand_kit); toast.success("Brand kit saved — applied across store campaigns & videos"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };
  return (
    <div className="sr-card" style={{ marginBottom: 20 }} data-testid="brand-kit-card">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <Palette size={16} style={{ color: "var(--blue)" }} />
        <strong style={{ color: "var(--navy)" }}>Brand &amp; look-and-feel</strong>
      </div>
      <p className="sr-muted" style={{ fontSize: 12, marginBottom: 12 }}>Save it once — your logo, colors and font apply across every store's campaigns and to videos you generate in the Growth Plan studio.</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12 }}>
        <div style={{ display: "grid", gap: 3 }}><label className="sr-label" style={{ fontSize: 12 }}>Logo URL</label>
          <input className="sr-input" value={bk.logo_url || ""} onChange={(e) => set("logo_url")(e.target.value)} placeholder="https://…/logo.png" data-testid="brand-logo-url" /></div>
        <div style={{ display: "grid", gap: 3 }}><label className="sr-label" style={{ fontSize: 12 }}>Primary color</label>
          <div style={{ display: "flex", gap: 6 }}>
            <input type="color" value={bk.primary_color || "#0B2447"} onChange={(e) => set("primary_color")(e.target.value)} data-testid="brand-primary-color" style={{ width: 44, height: 38, border: "1px solid var(--border)", borderRadius: 8, padding: 2, cursor: "pointer" }} />
            <input className="sr-input" value={bk.primary_color || ""} onChange={(e) => set("primary_color")(e.target.value)} style={{ flex: 1 }} /></div></div>
        <div style={{ display: "grid", gap: 3 }}><label className="sr-label" style={{ fontSize: 12 }}>Accent color</label>
          <div style={{ display: "flex", gap: 6 }}>
            <input type="color" value={bk.accent_color || "#E0A94A"} onChange={(e) => set("accent_color")(e.target.value)} data-testid="brand-accent-color" style={{ width: 44, height: 38, border: "1px solid var(--border)", borderRadius: 8, padding: 2, cursor: "pointer" }} />
            <input className="sr-input" value={bk.accent_color || ""} onChange={(e) => set("accent_color")(e.target.value)} style={{ flex: 1 }} /></div></div>
        <div style={{ display: "grid", gap: 3 }}><label className="sr-label" style={{ fontSize: 12 }}>Font</label>
          <select className="sr-select" value={bk.font || "Inter"} onChange={(e) => set("font")(e.target.value)} data-testid="brand-font">
            {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select></div>
        <div style={{ display: "grid", gap: 3, gridColumn: "1 / -1" }}><label className="sr-label" style={{ fontSize: 12 }}>Tagline</label>
          <input className="sr-input" value={bk.tagline || ""} onChange={(e) => set("tagline")(e.target.value)} placeholder="Your one-line brand promise" data-testid="brand-tagline" /></div>
      </div>
      {/* Live preview — how your videos will look */}
      <div style={{ marginTop: 14 }} data-testid="brand-preview">
        <div className="sr-label" style={{ fontSize: 12, marginBottom: 8 }}>Preview — how your videos will look</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 }}>
          {/* Ad frame with watermark */}
          <div>
            <div className="sr-muted" style={{ fontSize: 11, marginBottom: 4 }}>Your ad, watermarked</div>
            <div style={{ position: "relative", aspectRatio: "16 / 9", borderRadius: 10, overflow: "hidden", border: "1px solid var(--border)", background: "linear-gradient(135deg,#334155,#0f172a)" }} data-testid="brand-preview-watermark">
              <span style={{ position: "absolute", left: 12, bottom: 12, color: "#fff", fontWeight: 800, fontSize: 13, fontFamily: `${bk.font || "Inter"}, sans-serif`, textShadow: "0 1px 4px rgba(0,0,0,.5)" }}>Your video ad</span>
              {bk.logo_url
                ? <img src={bk.logo_url} alt="logo" onError={(e) => { e.currentTarget.style.display = "none"; }}
                    style={{ position: "absolute", top: 10, right: 10, height: 26, borderRadius: 5, background: "rgba(255,255,255,.9)", padding: 3 }} />
                : <span style={{ position: "absolute", top: 10, right: 10, width: 26, height: 26, borderRadius: 5, background: bk.accent_color || "#E0A94A" }} />}
            </div>
          </div>
          {/* Branded end card */}
          <div>
            <div className="sr-muted" style={{ fontSize: 11, marginBottom: 4 }}>Branded end card</div>
            <div style={{ position: "relative", aspectRatio: "16 / 9", borderRadius: 10, overflow: "hidden", border: "1px solid var(--border)", background: bk.primary_color || "#0B2447", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, padding: 12 }} data-testid="brand-preview-endcard">
              {bk.logo_url
                ? <img src={bk.logo_url} alt="logo" onError={(e) => { e.currentTarget.style.display = "none"; }} style={{ maxHeight: 34, maxWidth: "55%", background: "#fff", borderRadius: 6, padding: 4 }} />
                : <span style={{ width: 42, height: 42, borderRadius: "50%", background: bk.accent_color || "#E0A94A" }} />}
              <span style={{ color: "#fff", fontWeight: 800, fontSize: 13, textAlign: "center", fontFamily: `${bk.font || "Inter"}, sans-serif` }}>{bk.tagline || "Your brand promise"}</span>
              <span style={{ position: "absolute", left: 0, right: 0, bottom: 0, background: bk.accent_color || "#E0A94A", color: bk.primary_color || "#0B2447", fontWeight: 800, fontSize: 11.5, textAlign: "center", padding: "6px 0" }}>{bk.tagline ? "Learn more" : "Learn more"}</span>
            </div>
          </div>
        </div>
        <div className="sr-muted" style={{ fontSize: 11, marginTop: 6 }}>The logo corner-watermark and end card are added automatically to every AI video you generate.</div>
      </div>
      <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 12 }} onClick={save} disabled={saving} data-testid="brand-save-btn">
        {saving ? <Loader2 size={14} className="sr-spin" /> : <><Save size={14} /> Save brand kit</>}
      </button>
    </div>
  );
}

/* ---------------- Stores manager ---------------- */
function StoresManager({ ownerType, onChanged }) {
  const [stores, setStores] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", address: "", region: "", media_budget: "" });
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = () => sr.listStores().then((r) => setStores(r.stores || [])).catch(() => setStores([]));
  useEffect(() => { load(); }, []);

  const addStore = async () => {
    if (!form.name.trim()) { toast.error("Name the store"); return; }
    setBusy(true);
    try {
      await sr.addStore({ name: form.name.trim(), address: form.address.trim() || undefined, region: form.region.trim() || undefined, media_budget: Number(form.media_budget || 0) });
      toast.success("Store added");
      setForm({ name: "", address: "", region: "", media_budget: "" }); setAdding(false);
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add store"); }
    finally { setBusy(false); }
  };
  const removeStore = async (id) => {
    if (!window.confirm("Remove this store? Its saved targeting and competitors will be deleted.")) return;
    try { await sr.deleteStore(id); toast.success("Store removed"); load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };

  return (
    <div className="sr-card" style={{ marginBottom: 20 }} data-testid="stores-manager">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <StoreIco size={16} style={{ color: "var(--blue)" }} />
          <strong style={{ color: "var(--navy)" }}>Your stores</strong>
          <span className="sr-badge sr-badge-gray">{stores?.length ?? "…"}</span>
        </div>
        <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => setAdding((v) => !v)} data-testid="add-store-toggle">
          {adding ? "Cancel" : <><Plus size={14} /> Add store</>}
        </button>
      </div>
      <p className="sr-muted" style={{ fontSize: 12, marginBottom: 12 }}>Each store gets its own geo &amp; competitor targeting and its own offer — or push one offer to every store at once.</p>

      {adding && (
        <div style={{ background: "var(--blue-light)", borderRadius: 10, padding: 14, marginBottom: 14 }} data-testid="add-store-form">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 10 }}>
            <div style={{ display: "grid", gap: 2 }}><label className="sr-label" style={{ fontSize: 12 }}>Store name *</label><input className="sr-input" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} placeholder="Downtown" data-testid="store-name" /></div>
            <div style={{ display: "grid", gap: 2 }}><label className="sr-label" style={{ fontSize: 12 }}>Address</label><input className="sr-input" value={form.address} onChange={(e) => setForm((s) => ({ ...s, address: e.target.value }))} data-testid="store-address" /></div>
            <div style={{ display: "grid", gap: 2 }}><label className="sr-label" style={{ fontSize: 12 }}>Region</label><input className="sr-input" value={form.region} onChange={(e) => setForm((s) => ({ ...s, region: e.target.value }))} placeholder="West" data-testid="store-region" /></div>
            <div style={{ display: "grid", gap: 2 }}><label className="sr-label" style={{ fontSize: 12 }}>Budget $/mo</label><input className="sr-input" type="number" value={form.media_budget} onChange={(e) => setForm((s) => ({ ...s, media_budget: e.target.value }))} data-testid="store-budget-new" /></div>
          </div>
          <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 10 }} onClick={addStore} disabled={busy} data-testid="store-add-btn">
            {busy ? <Loader2 size={14} className="sr-spin" /> : <><Plus size={14} /> Add store</>}
          </button>
        </div>
      )}

      {stores === null ? <Loader2 size={16} className="sr-spin" />
        : stores.length === 0 ? <p className="sr-muted" style={{ fontSize: 13 }} data-testid="stores-empty">No stores yet. Add your first location above.</p>
          : (
            <div style={{ display: "grid", gap: 12 }} data-testid="stores-list">
              {stores.map((st) => (
                <StoreCard key={st.location_id} store={st} expanded={expanded === st.location_id}
                  onToggle={() => setExpanded(expanded === st.location_id ? null : st.location_id)}
                  onSaved={() => { load(); onChanged?.(); }} onRemove={() => removeStore(st.location_id)}
                  onApplyAll={load} />
              ))}
            </div>
          )}
    </div>
  );
}

function StoreCard({ store, expanded, onToggle, onSaved, onRemove, onApplyAll }) {
  const [budget, setBudget] = useState(store.media_budget || 0);
  const [region, setRegion] = useState(store.region || "");
  const [targeting, setTargeting] = useState(store.targeting || {});
  const [offer, setOffer] = useState(store.offer || { headline: "", discount: "", cta: "" });
  const [creativeMode, setCreativeMode] = useState(store.creative_mode || "shared");
  const [saving, setSaving] = useState("");
  const [comps, setComps] = useState(null);

  const geo = targeting?.geo || null;

  const saveBasics = async () => {
    setSaving("basics");
    try { await sr.updateStore(store.location_id, { media_budget: Number(budget), region, offer, creative_mode: creativeMode }); toast.success("Store saved"); onSaved?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(""); }
  };
  const saveTargeting = async () => {
    setSaving("targeting");
    try { await sr.setStoreTargeting(store.location_id, targeting); toast.success("Targeting saved for this store"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save targeting"); }
    finally { setSaving(""); }
  };
  const discover = async () => {
    if (!store.lat || !store.lng) { toast.error("This store needs a verified address (with map coordinates) to conquest competitors around it. Add it via business search first."); return; }
    setSaving("discover");
    try {
      const r = await sr.discoverCompetitors(store.location_id, 5000, store.category || "");
      setComps(r.count || 0);
      toast.success(`${r.count} competitor location(s) mapped around ${store.name} — conquesting ready`);
      setTargeting((t) => ({ ...t, mode: "competitor", conquesting: true }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not discover competitors"); }
    finally { setSaving(""); }
  };
  const applyOfferToAll = async () => {
    if (!offer.headline?.trim()) { toast.error("Write an offer headline first"); return; }
    if (!window.confirm("Apply this offer to every store?")) return;
    setSaving("applyall");
    try { const r = await sr.applyOfferAll(offer); toast.success(`Offer pushed to ${r.stores_updated} store(s)`); onApplyAll?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not apply to all"); }
    finally { setSaving(""); }
  };

  return (
    <div className="sr-card" data-testid={`store-${store.location_id}`} style={{ background: "#fff" }}>
      <button onClick={onToggle} data-testid={`store-toggle-${store.location_id}`}
        style={{ width: "100%", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 10, textAlign: "left", padding: 0 }}>
        <span className="sr-pill-ico" style={{ background: "var(--blue-light)", color: "var(--blue)" }}><StoreIco size={16} /></span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ color: "var(--navy)", fontWeight: 800, fontSize: 16, display: "block" }}>{store.name}</span>
          <span className="sr-muted" style={{ fontSize: 12 }}>{store.address || "No address"} · {money(store.media_budget)}/mo · {store.campaigns} campaigns{store.region ? ` · ${store.region}` : ""}</span>
        </span>
        {store.offer?.headline && <span className="sr-badge sr-badge-green" style={{ fontSize: 10 }}><Tag size={10} /> Offer set</span>}
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {expanded && (
        <div style={{ marginTop: 14, borderTop: "1px solid var(--border)", paddingTop: 14, display: "grid", gap: 16 }}>
          {/* Basics */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div className="sr-field" style={{ margin: 0 }}><label className="sr-label" style={{ fontSize: 12 }}>Media budget ($/mo)</label>
              <input className="sr-input" type="number" style={{ width: 140 }} value={budget} onChange={(e) => setBudget(e.target.value)} data-testid={`store-budget-${store.location_id}`} /></div>
            <div className="sr-field" style={{ margin: 0 }}><label className="sr-label" style={{ fontSize: 12 }}>Region</label>
              <input className="sr-input" style={{ width: 150 }} value={region} onChange={(e) => setRegion(e.target.value)} placeholder="e.g. West" data-testid={`store-region-${store.location_id}`} /></div>
            <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={onRemove} data-testid={`store-remove-${store.location_id}`} style={{ marginLeft: "auto", color: "#B42318" }}><Trash2 size={14} /> Remove</button>
          </div>

          {/* Per-store targeting + competitor conquesting */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <Target size={15} style={{ color: "var(--blue)" }} />
              <strong style={{ color: "var(--navy)", fontSize: 14 }}>Targeting for this store</strong>
              <button className="sr-btn sr-btn-sm" onClick={discover} disabled={saving === "discover"} data-testid={`store-conquest-${store.location_id}`} style={{ marginLeft: "auto" }}>
                {saving === "discover" ? <Loader2 size={13} className="sr-spin" /> : <><Crosshair size={13} /> Conquest competitors nearby</>}
              </button>
            </div>
            {comps != null && <p className="sr-muted" style={{ fontSize: 12, marginTop: -2, marginBottom: 8 }}><MapPin size={11} style={{ verticalAlign: -1 }} /> {comps} competitor location(s) mapped around this store.</p>}
            <AdvancedTargeting geo={geo} value={targeting} onChange={setTargeting} defaultOpen />
            <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 10 }} onClick={saveTargeting} disabled={saving === "targeting"} data-testid={`store-save-targeting-${store.location_id}`}>
              {saving === "targeting" ? <Loader2 size={13} className="sr-spin" /> : <><Save size={13} /> Save targeting</>}
            </button>
          </div>

          {/* Per-store offer & creative */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <Tag size={15} style={{ color: "var(--blue)" }} />
              <strong style={{ color: "var(--navy)", fontSize: 14 }}>Offer &amp; creative</strong>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              {["shared", "custom"].map((m) => (
                <button key={m} onClick={() => setCreativeMode(m)} data-testid={`store-creative-mode-${m}-${store.location_id}`}
                  style={{ border: "1px solid " + (creativeMode === m ? "var(--blue)" : "var(--border)"), background: creativeMode === m ? "var(--blue)" : "#fff", color: creativeMode === m ? "#fff" : "var(--navy)", borderRadius: 999, padding: "6px 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
                  {m === "shared" ? "Same brand creative" : "Custom for this store"}
                </button>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 10 }}>
              <div style={{ display: "grid", gap: 2 }}><label className="sr-label" style={{ fontSize: 12 }}>Offer headline</label><input className="sr-input" value={offer.headline || ""} onChange={(e) => setOffer((o) => ({ ...o, headline: e.target.value }))} placeholder="20% off lunch" data-testid={`store-offer-headline-${store.location_id}`} /></div>
              <div style={{ display: "grid", gap: 2 }}><label className="sr-label" style={{ fontSize: 12 }}>Discount</label><input className="sr-input" value={offer.discount || ""} onChange={(e) => setOffer((o) => ({ ...o, discount: e.target.value }))} placeholder="20% / $10 off" data-testid={`store-offer-discount-${store.location_id}`} /></div>
              <div style={{ display: "grid", gap: 2 }}><label className="sr-label" style={{ fontSize: 12 }}>Call to action</label><input className="sr-input" value={offer.cta || ""} onChange={(e) => setOffer((o) => ({ ...o, cta: e.target.value }))} placeholder="Order now" data-testid={`store-offer-cta-${store.location_id}`} /></div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={saveBasics} disabled={saving === "basics"} data-testid={`store-save-${store.location_id}`}>
                {saving === "basics" ? <Loader2 size={13} className="sr-spin" /> : <><Save size={13} /> Save this store</>}
              </button>
              <button className="sr-btn sr-btn-sm" onClick={applyOfferToAll} disabled={saving === "applyall"} data-testid={`store-apply-offer-all-${store.location_id}`}>
                {saving === "applyall" ? <Loader2 size={13} className="sr-spin" /> : <><Share2 size={13} /> Deploy this offer to all stores</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value, accent, icon: Icon }) => (
  <div className="sr-card" style={accent ? { background: "var(--blue-light)", border: "1px solid var(--blue)" } : {}}>
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      {Icon && <Icon size={13} style={{ color: "var(--blue)" }} />}
      <span className="sr-muted" style={{ fontSize: 12, fontWeight: 600 }}>{label}</span>
    </div>
    <div style={{ fontSize: 24, fontWeight: 800, color: "var(--navy)", marginTop: 4 }}>{value ?? "—"}</div>
  </div>
);

/* ---------------- Franchisor: co-op campaigns ---------------- */
function CoopCampaigns({ coop, reload }) {
  const [busy, setBusy] = useState(null);
  if (!coop) return null;
  const markCoop = async (cid, val) => {
    setBusy(cid);
    try { await sr.coopMark(cid, val); toast.success(val ? "Marked as co-op" : "Removed from co-op"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    finally { setBusy(null); }
  };
  const approveCoop = async (cid) => {
    setBusy(cid);
    try { const r = await sr.coopApprove(cid); toast.success(`Approved & pushed to ${r.locations_covered} location${r.locations_covered === 1 ? "" : "s"}`); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not approve"); }
    finally { setBusy(null); }
  };
  return (
    <div className="sr-card" style={{ marginBottom: 20 }} data-testid="franchise-coop">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <Share2 size={16} style={{ color: "var(--blue)" }} />
        <strong style={{ color: "var(--navy)" }}>Co-op brand campaigns</strong>
      </div>
      <p className="sr-muted" style={{ fontSize: 12, marginBottom: 12 }}>Approve a shared brand campaign once — it applies to all {coop.location_count} location{coop.location_count === 1 ? "" : "s"} at once.</p>
      {coop.campaigns.length === 0 ? (
        <p className="sr-muted" style={{ fontSize: 13 }}>No campaigns yet.</p>
      ) : (
        <div style={{ display: "grid", gap: 8, maxHeight: 320, overflowY: "auto" }} data-testid="coop-list">
          {coop.campaigns.filter((c) => c.coop || c.locations_covered > 0).concat(coop.campaigns.filter((c) => !c.coop && !c.locations_covered).slice(0, 6)).map((c) => (
            <div key={c.id} className="sr-card" data-testid={`coop-${c.id}`} style={{ background: "#fff", display: "flex", alignItems: "center", gap: 12, padding: "10px 14px" }}>
              <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                <div style={{ fontSize: 14, color: "var(--navy)", fontWeight: 600, textTransform: "capitalize" }}>{c.name}</div>
                <div className="sr-muted" style={{ fontSize: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {c.coop && <span className="sr-badge sr-badge-amber" style={{ fontSize: 10 }}>Co-op</span>}
                  {c.coop_approved_at
                    ? <span className="sr-badge sr-badge-green" style={{ fontSize: 10 }}><CheckCircle2 size={10} /> Approved · {c.locations_covered} locations</span>
                    : <span>{c.channel}</span>}
                </div>
              </div>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => markCoop(c.id, !c.coop)} disabled={busy === c.id} data-testid={`coop-mark-${c.id}`}>{c.coop ? "Unmark" : "Mark co-op"}</button>
              {c.coop && (
                <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => approveCoop(c.id)} disabled={busy === c.id} data-testid={`coop-approve-${c.id}`}>
                  {busy === c.id ? <Loader2 size={14} className="sr-spin" /> : <><CheckCircle2 size={14} /> Approve for all</>}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Franchisor: corporate templates ---------------- */
const FIELD_LABELS = { headline: "Headline", offer: "Offer", final_url: "Landing URL", budget_monthly: "Budget" };
const PAYER_LABEL = { corporate: "Corporate pays", location: "Location pays", split: "Split" };
const money2 = (n) => "$" + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

function CorporateTemplates({ stores }) {
  const [tpls, setTpls] = useState(null);
  const [form, setForm] = useState({ name: "", channel: "Display", headline: "", offer: "", final_url: "", budget_monthly: "", locked_fields: [], payer_mode: "corporate", payer_corporate_pct: 50 });
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deployFor, setDeployFor] = useState(null);
  const [targets, setTargets] = useState({});

  const load = () => sr.franchiseTemplates().then(setTpls).catch(() => setTpls({ templates: [] }));
  useEffect(() => { load(); }, []);

  const toggleLocked = (f) => setForm((s) => ({ ...s, locked_fields: s.locked_fields.includes(f) ? s.locked_fields.filter((x) => x !== f) : [...s.locked_fields, f] }));
  const blankForm = { name: "", channel: "Display", headline: "", offer: "", final_url: "", budget_monthly: "", locked_fields: [], payer_mode: "corporate", payer_corporate_pct: 50 };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name the template"); return; }
    setBusy(true);
    try {
      const body = { ...form, budget_monthly: Number(form.budget_monthly || 0), payer_corporate_pct: Number(form.payer_corporate_pct) };
      if (editingId) { await sr.updateFranchiseTemplate(editingId, body); toast.success("Template updated — re-push to apply it to running locations"); }
      else { await sr.createFranchiseTemplate(body); toast.success("Corporate template created"); }
      setForm(blankForm); setEditingId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save template"); }
    finally { setBusy(false); }
  };
  const startEdit = (tpl) => {
    setEditingId(tpl.id);
    setForm({ name: tpl.name || "", channel: tpl.channel || "Display", headline: tpl.headline || "", offer: tpl.offer || "", final_url: tpl.final_url || "", budget_monthly: tpl.budget_monthly ?? "", locked_fields: tpl.locked_fields || [], payer_mode: tpl.payer?.mode || "corporate", payer_corporate_pct: tpl.payer?.corporate_pct ?? 50 });
  };
  const repush = async (tpl) => {
    if (!window.confirm("Re-push this template's latest copy to every location running it? Those campaigns return to pending approval.")) return;
    try { const r = await sr.repushFranchiseTemplate(tpl.id); toast.success(`Re-pushed to ${r.updated} location${r.updated === 1 ? "" : "s"}`); load(); } catch (e) { toast.error("Could not re-push"); }
  };
  const duplicate = async (tpl) => {
    try { await sr.duplicateFranchiseTemplate(tpl.id); toast.success("Template duplicated"); load(); } catch (e) { toast.error("Could not duplicate"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this template? Any campaigns deployed from it will be archived.")) return;
    try { const r = await sr.deleteFranchiseTemplate(id); toast.success(r.archived_campaigns ? `Template deleted · ${r.archived_campaigns} campaign(s) archived` : "Template deleted"); load(); } catch (e) { toast.error("Could not delete"); }
  };
  const openDeploy = (tpl) => {
    setDeployFor(deployFor === tpl.id ? null : tpl.id);
    const init = {};
    (stores || []).forEach((s) => { init[s.location_id] = { checked: false, budget: s.media_budget }; });
    setTargets(init);
  };
  const deploy = async (tpl) => {
    const chosen = Object.entries(targets).filter(([, v]) => v.checked)
      .map(([lid, v]) => ({ location_id: lid, budget_monthly: Number(v.budget), final_url: v.final_url || undefined }));
    if (!chosen.length) { toast.error("Pick at least one location"); return; }
    setBusy(true);
    try { const r = await sr.deployFranchiseTemplate(tpl.id, chosen); toast.success(`Deployed to ${r.deployed} location${r.deployed === 1 ? "" : "s"}`); setDeployFor(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not deploy"); }
    finally { setBusy(false); }
  };

  if (!tpls) return null;
  const budgetLockedFor = (tpl) => (tpl.locked_fields || []).includes("budget_monthly");

  return (
    <div className="sr-card" style={{ marginBottom: 20 }} data-testid="franchise-templates">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <LayoutTemplate size={16} style={{ color: "var(--blue)" }} />
        <strong style={{ color: "var(--navy)" }}>Corporate templates</strong>
      </div>
      <p className="sr-muted" style={{ fontSize: 12, marginBottom: 12 }}>Build a brand campaign once, lock the fields locations can't change, set who pays, and roll it out to any store in a click.</p>

      <div style={{ background: "var(--blue-light)", borderRadius: 10, padding: 14, marginBottom: 14 }} data-testid="template-create">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10 }}>
          <div style={{ display: "grid", gap: 2 }}><label className="sr-label" style={{ fontSize: 12 }}>Template name</label><input className="sr-input" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} placeholder="Fall Value Menu" data-testid="template-name" /></div>
          <div style={{ display: "grid", gap: 2 }}><label className="sr-label" style={{ fontSize: 12 }}>Channel</label>
            <select className="sr-input" value={form.channel} onChange={(e) => setForm((s) => ({ ...s, channel: e.target.value }))} data-testid="template-channel">
              {["Display", "Social", "Google Search", "Meta", "Radio"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select></div>
          <div style={{ display: "grid", gap: 2 }}><label className="sr-label" style={{ fontSize: 12 }}>Monthly budget</label><input className="sr-input" type="number" value={form.budget_monthly} onChange={(e) => setForm((s) => ({ ...s, budget_monthly: e.target.value }))} placeholder="2500" data-testid="template-budget" /></div>
          <div style={{ display: "grid", gap: 2 }}><label className="sr-label" style={{ fontSize: 12 }}>Headline</label><input className="sr-input" value={form.headline} onChange={(e) => setForm((s) => ({ ...s, headline: e.target.value }))} data-testid="template-headline" /></div>
          <div style={{ display: "grid", gap: 2 }}><label className="sr-label" style={{ fontSize: 12 }}>Offer</label><input className="sr-input" value={form.offer} onChange={(e) => setForm((s) => ({ ...s, offer: e.target.value }))} data-testid="template-offer" /></div>
          <div style={{ display: "grid", gap: 2 }}><label className="sr-label" style={{ fontSize: 12 }}>Landing URL</label><input className="sr-input" value={form.final_url} onChange={(e) => setForm((s) => ({ ...s, final_url: e.target.value }))} data-testid="template-url" /></div>
        </div>
        <div style={{ marginTop: 12, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
          <div>
            <div className="sr-label" style={{ fontSize: 12, marginBottom: 4 }}><Lock size={11} style={{ verticalAlign: -1 }} /> Lock fields (locations can't change)</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }} data-testid="template-locked-fields">
              {Object.entries(FIELD_LABELS).map(([f, label]) => (
                <label key={f} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, color: "var(--navy)" }}>
                  <input type="checkbox" checked={form.locked_fields.includes(f)} onChange={() => toggleLocked(f)} data-testid={`template-lock-${f}`} /> {label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <div className="sr-label" style={{ fontSize: 12, marginBottom: 4 }}><CreditCard size={11} style={{ verticalAlign: -1 }} /> Payer</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <select className="sr-input" style={{ width: 150 }} value={form.payer_mode} onChange={(e) => setForm((s) => ({ ...s, payer_mode: e.target.value }))} data-testid="template-payer-mode">
                {["corporate", "location", "split"].map((m) => <option key={m} value={m}>{PAYER_LABEL[m]}</option>)}
              </select>
              {form.payer_mode === "split" && (
                <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13 }}>
                  <input className="sr-input" style={{ width: 70 }} type="number" value={form.payer_corporate_pct} onChange={(e) => setForm((s) => ({ ...s, payer_corporate_pct: e.target.value }))} data-testid="template-split-pct" />% corporate
                </span>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
          <button className="sr-btn sr-btn-primary sr-btn-sm" disabled={busy} onClick={save} data-testid="template-create-btn"><Plus size={14} /> {editingId ? "Save changes" : "Create template"}</button>
          {editingId && <button className="sr-btn sr-btn-ghost sr-btn-sm" disabled={busy} onClick={() => { setEditingId(null); setForm(blankForm); }} data-testid="template-cancel-btn">Cancel</button>}
        </div>
      </div>

      {(tpls.templates || []).length === 0 ? (
        <p className="sr-muted" style={{ fontSize: 13 }} data-testid="templates-empty">No corporate templates yet. Create one above to standardize campaigns across stores.</p>
      ) : (
        <div style={{ display: "grid", gap: 10 }} data-testid="templates-list">
          {tpls.templates.map((tpl) => (
            <div key={tpl.id} className="sr-card" data-testid={`template-${tpl.id}`} style={{ background: "#fff" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontWeight: 800, color: "var(--navy)" }}>{tpl.name}</div>
                  <div className="sr-muted" style={{ fontSize: 12, marginTop: 2 }}>{tpl.channel} · {money2(tpl.budget_monthly)}/mo · deployed to {tpl.deployed_count}</div>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => startEdit(tpl)} data-testid={`template-edit-${tpl.id}`}>Edit</button>
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => duplicate(tpl)} data-testid={`template-duplicate-${tpl.id}`}><Copy size={13} /></button>
                  {tpl.deployed_count > 0 && <button className="sr-btn sr-btn-sm" onClick={() => repush(tpl)} data-testid={`template-repush-${tpl.id}`}><Share2 size={13} /> Re-push</button>}
                  <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => openDeploy(tpl)} data-testid={`template-deploy-${tpl.id}`}><Send size={13} /> Deploy</button>
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => del(tpl.id)} data-testid={`template-delete-${tpl.id}`}><Trash2 size={13} /></button>
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                <span className="sr-badge" style={{ background: "#EEF4FF", color: "var(--blue)" }} data-testid={`template-payer-${tpl.id}`}><CreditCard size={11} /> {PAYER_LABEL[tpl.payer?.mode] || "Corporate pays"}{tpl.payer?.mode === "split" ? ` ${tpl.payer.corporate_pct}/${tpl.payer.location_pct}` : ""}</span>
                {(tpl.locked_fields || []).length === 0
                  ? <span className="sr-badge sr-badge-gray">No locked fields</span>
                  : (tpl.locked_fields || []).map((f) => <span key={f} className="sr-badge sr-badge-amber"><Lock size={10} /> {FIELD_LABELS[f] || f}</span>)}
              </div>

              {deployFor === tpl.id && (
                <div style={{ marginTop: 12, borderTop: "1px dashed var(--border)", paddingTop: 12 }} data-testid={`deploy-panel-${tpl.id}`}>
                  <div className="sr-label" style={{ fontSize: 12, marginBottom: 8 }}>Deploy to stores{budgetLockedFor(tpl) ? " (budget locked by corporate)" : ""}</div>
                  {(stores || []).length === 0 ? (
                    <p className="sr-muted" style={{ fontSize: 12 }}>Add stores first.</p>
                  ) : (stores || []).map((s) => (
                    <div key={s.location_id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "4px 0" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--navy)", flex: 1 }}>
                        <input type="checkbox" checked={!!targets[s.location_id]?.checked} onChange={(e) => setTargets((t) => ({ ...t, [s.location_id]: { ...t[s.location_id], checked: e.target.checked } }))} data-testid={`deploy-loc-${s.location_id}`} />
                        {s.name}
                      </label>
                      {!budgetLockedFor(tpl) && (
                        <span style={{ fontSize: 12 }}>$<input className="sr-input" style={{ width: 100, display: "inline-block" }} type="number" value={targets[s.location_id]?.budget ?? s.media_budget} onChange={(e) => setTargets((t) => ({ ...t, [s.location_id]: { ...t[s.location_id], budget: e.target.value } }))} data-testid={`deploy-budget-${s.location_id}`} />/mo</span>
                      )}
                    </div>
                  ))}
                  <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 10 }} disabled={busy} onClick={() => deploy(tpl)} data-testid={`deploy-confirm-${tpl.id}`}><Send size={13} /> Deploy now</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
